import React, { useState } from 'react';
import { Box, Button, List, Typography, useTheme } from '@mui/material';
import { CardItem, CardItemAction, InformationCardItem } from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { DossierDocumentSign } from '../../../providers/types';

export type DossierSignDetailProps = {
    dossier: DossierDocumentSign;
    actions: Array<{
        label: string;
        icon: IconName;
        onClick: () => void;
    }>;
    onDownloadDocument: (fileName: string, fileId: number) => void;
    onShowDocument: (fileName: string, fileId: number) => Promise<void>;
};

const DossierSignDetail: React.FunctionComponent<DossierSignDetailProps> = function (props) {
    const { dossier, actions, onDownloadDocument, onShowDocument } = props;
    const { palette } = useTheme();
    const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(null);

    const goToSignUrl = (url: string) => {
        window.open(url, '_blank');
    };

    return (
        <Box
            sx={{
                padding: '0 30px 30px 30px',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '1em' }}>
                {actions.length > 0 && (
                    <Box sx={{ display: 'flex', columnGap: '1em' }}>
                        {actions.map((action) => (
                            <Button
                                key={action.label}
                                variant="text"
                                onClick={action.onClick}
                                sx={{ display: 'flex', columnGap: '0.5em', color: 'text.gris50' }}
                            >
                                <FaOptionIcon icon={['fasl', action.icon]} />
                                {action.label}
                            </Button>
                        ))}
                    </Box>
                )}
                <Typography variant="l2dh7">Informations générales</Typography>
                <List
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px 0',
                    }}
                >
                    <InformationCardItem
                        label="Sujet"
                        value={dossier.subject}
                    />
                    <InformationCardItem
                        label="Référence externe"
                        value={dossier.reference}
                    />
                    <InformationCardItem
                        label="Demandeur"
                        value={`${dossier.prescriber.name} - ${dossier.prescriber.email}`}
                    />
                    <InformationCardItem
                        label="Créer par"
                        value={`${dossier.createdBy.name} - ${dossier.createdBy.reference}`}
                    />
                    <InformationCardItem
                        label="Créé le"
                        value={dossier.createdAt?.toLocaleDateString()}
                    />
                    <InformationCardItem
                        label="Dernière mise à jour"
                        value={dossier.updatedAt?.toLocaleDateString()}
                    />
                </List>
                <Typography variant="l2dh7">Documents</Typography>
                <Box
                    sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '10px' }}
                >
                    {dossier.documents.map((document) => {
                        const documentActions: Array<CardItemAction> = [];
                        if (document.signed) {
                            documentActions.push({
                                label: 'Aperçu du document',
                                icon: 'eye',
                                loading: loadingDocumentId === document.id,
                                disabled: loadingDocumentId !== null,
                                onClick: () => {
                                    if (!document?.signedFileId || loadingDocumentId !== null) {
                                        return;
                                    }
                                    setLoadingDocumentId(document.id);
                                    onShowDocument(
                                        document.fileName,
                                        document.signedFileId,
                                    ).finally(() => setLoadingDocumentId(null));
                                },
                            });
                            documentActions.push({
                                label: 'Télécharger le document',
                                icon: 'download',
                                onClick: () => {
                                    if (document?.signedFileId) {
                                        onDownloadDocument(
                                            document.fileName,
                                            document.signedFileId,
                                        );
                                    }
                                },
                            });
                        }

                        return (
                            <CardItem
                                key={document.name}
                                title={`${document.name}`}
                                icon="file"
                                color={palette.primary}
                                actions={documentActions}
                            />
                        );
                    })}
                </Box>
                <Typography variant="l2dh7">Destinataires</Typography>
                <Box
                    sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '10px' }}
                >
                    {dossier.recipients.map((recipient) => {
                        const recipientActions: Array<CardItemAction> = [];

                        let color = recipient.signed ? palette.success : palette.warning;
                        let status = recipient.signed ? 'Signé' : 'En attente';

                        if (recipient.refused) {
                            color = palette.error;
                            status = 'Refusé';
                        }

                        if (!recipient.signed && !recipient.refused) {
                            recipientActions.push({
                                label: 'Lien de signature',
                                icon: 'arrow-up-right-from-square',
                                onClick: () => goToSignUrl(recipient.link),
                            });
                        }

                        return (
                            <CardItem
                                key={`${recipient.firstName} ${recipient.lastName}`}
                                title={`${recipient.firstName} ${recipient.lastName}`}
                                icon="user"
                                color={color}
                                additionalInfos={[recipient.email, recipient.mobile, status]}
                                actions={recipientActions}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default DossierSignDetail;
